import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const COLORS = [
  'rgb(0, 122, 255)',
  'rgb(52, 199, 89)',
  'rgb(255, 149, 0)',
  'rgb(88, 86, 214)',
  'rgb(255, 59, 48)',
  'rgb(175, 82, 222)',
  'rgb(255, 45, 85)',
  'rgb(90, 200, 250)',
];

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover/95 backdrop-blur-md border border-border rounded-lg shadow-xl p-3">
        <p className="text-sm font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatter ? formatter(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const SpendChart = ({ data, title, className }) => {
  const formatCurrency = (value) => `$${value.toLocaleString()}`;

  if (!data || data.length === 0) {
    return (
      <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="spend-chart">
        <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {title || 'Spend Over Time'}
        </h3>
        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
          No data available
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="spend-chart">
      <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {title || 'Spend Over Time'}
      </h3>
      <div className="h-[250px] w-full min-w-0">
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgb(0, 122, 255)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="rgb(0, 122, 255)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" opacity={0.5} />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 11, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => value.slice(5)}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="rgb(0, 122, 255)"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorSpend)"
              name="Daily Spend"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export const TopServicesChart = ({ data, title, className }) => {
  const formatCurrency = (value) => `$${value.toLocaleString()}`;

  if (!data || data.length === 0) {
    return (
      <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="top-services-chart">
        <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {title || 'Top Services by Cost'}
        </h3>
        <div className="h-[300px] flex items-center justify-center text-muted-foreground">
          No data available
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="top-services-chart">
      <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {title || 'Top Services by Cost'}
      </h3>
      <div className="h-[300px] w-full min-w-0">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" opacity={0.5} horizontal={false} />
            <XAxis 
              type="number"
              tick={{ fontSize: 11, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={100}
            />
            <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
            <Bar dataKey="cost" name="Cost" radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export const LicenseChart = ({ data, title, className }) => {
  if (!data || data.length === 0) {
    return (
      <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="license-chart">
        <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {title || 'License Allocation'}
        </h3>
        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
          No data available
        </div>
      </Card>
    );
  }

  const chartData = data.map(license => ({
    name: license.sku_name?.replace('Microsoft 365 ', 'M365 ') || license.sku_part_number,
    assigned: license.assigned,
    available: license.available,
  }));

  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="license-chart">
      <h3 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {title || 'License Allocation'}
      </h3>
      <div className="h-[250px] w-full min-w-0">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" opacity={0.5} />
            <XAxis 
              dataKey="name"
              tick={{ fontSize: 10, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              angle={-20}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'rgb(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="assigned" name="Assigned" fill="rgb(0, 122, 255)" stackId="stack" radius={[0, 0, 0, 0]} />
            <Bar dataKey="available" name="Available" fill="rgb(52, 199, 89)" stackId="stack" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export const StorageChart = ({ data, title, className }) => {
  const chartData = [
    { name: 'Used', value: data?.used_tb || 0 },
    { name: 'Available', value: data?.available_tb || 0 },
  ];

  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="storage-chart">
      <h3 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {title || 'SharePoint Storage'}
      </h3>
      <div className="flex items-center gap-6">
        <div className="h-[150px] w-[150px] min-w-[150px]">
          <ResponsiveContainer width={150} height={150}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={65}
                paddingAngle={2}
                dataKey="value"
              >
                <Cell fill="rgb(0, 122, 255)" />
                <Cell fill="rgb(var(--muted))" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-3">
          <div>
            <p className="text-sm text-muted-foreground">Used</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {data?.used_tb?.toFixed(1)} TB
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-lg font-semibold text-muted-foreground">
              {data?.total_tb?.toFixed(1)} TB
            </p>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${data?.percent_used || 0}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{data?.percent_used?.toFixed(1)}% used</p>
        </div>
      </div>
    </Card>
  );
};
